import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Copy, KeyRound, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/errors";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
} from "@/queries/api-keys";
import { useOrgs, useProjects, useUserProjects } from "@/queries/orgs";
import { useUser } from "@/queries/user";
import type { ApiKeyScope, CreatedApiKey } from "@/types";
import { cn } from "@/lib/utils";

const PERSONAL_SCOPE = "__personal__";

const ALL_SCOPES: ApiKeyScope[] = [
  "tasks:read",
  "tasks:write",
  "tasks:operate",
];

export function ApiKeysPage() {
  const keys = useApiKeys();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();

  const orgs = useOrgs();
  const user = useUser();
  const userId = user.data?.user_id ?? "";
  // "scope" picks where to source projects from: personal user-owned, or one of the orgs.
  const [scope, setScope] = useState<string>("");
  const [projectId, setProjectId] = useState<string>("");
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiKeyScope[]>(["tasks:read"]);
  const [revealed, setRevealed] = useState<CreatedApiKey | null>(null);

  const personalProjects = useUserProjects(userId);
  const orgProjects = useProjects(scope === PERSONAL_SCOPE ? "" : scope);
  const projectChoices =
    scope === PERSONAL_SCOPE ? personalProjects.data : orgProjects.data;

  const projectLabelById = useMemo(() => {
    const map = new Map<string, string>();
    personalProjects.data?.forEach((p) => map.set(p.id, p.name));
    orgProjects.data?.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [personalProjects.data, orgProjects.data]);

  function toggleScope(s: ApiKeyScope) {
    setScopes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !projectId || scopes.length === 0) return;
    create.mutate(
      {
        name: name.trim(),
        project_id: projectId,
        scopes,
      },
      {
        onSuccess: (key) => {
          setRevealed(key);
          setName("");
          setProjectId("");
          setScopes(["tasks:read"]);
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      },
    );
  }

  function copyKey(plaintext: string) {
    navigator.clipboard.writeText(plaintext).then(
      () => toast.success("API key copied to clipboard"),
      () => toast.error("Could not copy - copy manually"),
    );
  }

  function confirmRevoke(id: string, prefix: string) {
    if (!confirm(`Revoke key ${prefix}…? Agents using it will start failing.`))
      return;
    revoke.mutate(id, {
      onSuccess: () => toast.success(`Key ${prefix}… revoked`),
      onError: (err) => toast.error(getErrorMessage(err)),
    });
  }

  return (
    <div className="space-y-6">
      <Helmet><title>TodoLLM - API Keys</title></Helmet>
      <header className="space-y-1">
        <h2 className="text-3xl font-semibold tracking-tight">API keys</h2>
        <p className="text-sm text-muted-foreground">
          Server-issued credentials for MCP agents. Each key is shown once at
          creation time - copy it then.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="size-4" />
            New API key
          </CardTitle>
          <CardDescription>
            Scope each key to the smallest org/project + permission set the
            agent needs.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  placeholder="laptop-claude-dev"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="key-scope">Scope</Label>
                <Select
                  value={scope}
                  onValueChange={(v) => {
                    setScope(v);
                    setProjectId("");
                  }}
                >
                  <SelectTrigger id="key-scope" className="w-full">
                    <SelectValue placeholder="Personal or org…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PERSONAL_SCOPE}>
                      Personal projects
                    </SelectItem>
                    {orgs.data?.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="key-project">Project</Label>
                <Select
                  value={projectId}
                  onValueChange={setProjectId}
                  disabled={!scope}
                >
                  <SelectTrigger id="key-project" className="w-full">
                    <SelectValue
                      placeholder={
                        scope ? "Select project" : "Pick a scope first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {projectChoices?.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Scopes</Label>
              <div className="flex flex-wrap gap-2">
                {ALL_SCOPES.map((s) => {
                  const active = scopes.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleScope(s)}
                      className={cn(
                        "rounded-md border px-3 py-1.5 font-mono text-xs transition-colors",
                        active
                          ? "border-primary bg-primary/15 text-primary"
                          : "border-border bg-input/30 text-muted-foreground hover:text-foreground",
                      )}
                      aria-pressed={active}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              type="submit"
              disabled={
                create.isPending ||
                !name.trim() ||
                !projectId ||
                scopes.length === 0
              }
            >
              <KeyRound className="size-4" />
              {create.isPending ? "Generating…" : "Generate key"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your keys</CardTitle>
          <CardDescription>
            Plaintext is never stored - only the prefix and an argon2 hash.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {keys.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : keys.data && keys.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {keys.data.map((k) => {
                const isRevoked = !!k.revoked_at;
                return (
                  <li
                    key={k.id}
                    className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{k.name}</span>
                        {isRevoked ? (
                          <Badge variant="destructive">revoked</Badge>
                        ) : (
                          <Badge variant="success">active</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <code className="font-mono">
                          sk_todollm_{k.prefix}…
                        </code>
                        <span>·</span>
                        <span>
                          project{" "}
                          {projectLabelById.get(k.project_id) ??
                            k.project_id.slice(0, 8)}
                        </span>
                        <span>·</span>
                        <span>
                          {k.last_used_at
                            ? `last used ${new Date(k.last_used_at).toLocaleString()}`
                            : "never used"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {k.scopes.map((s) => (
                          <Badge
                            key={s}
                            variant="outline"
                            className="font-mono"
                          >
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {!isRevoked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => confirmRevoke(k.id, k.prefix)}
                      >
                        <Trash2 className="size-4" />
                        Revoke
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No keys yet.</p>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!revealed}
        onOpenChange={(o) => {
          if (!o) setRevealed(null);
        }}
      >
        {revealed && (
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="size-5 text-primary" />
                Copy this key now
              </DialogTitle>
              <DialogDescription>
                This is the only time the full secret is shown. After you close
                this dialog, only the prefix is recoverable.
              </DialogDescription>
            </DialogHeader>
            <div className="min-w-0 space-y-3">
              <div className="flex items-stretch gap-2">
                <code className="min-w-0 flex-1 break-all rounded-md border border-border bg-input/30 px-3 py-2 font-mono text-sm">
                  {revealed.plaintext}
                </code>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => copyKey(revealed.plaintext)}
                >
                  <Copy className="size-4" />
                  Copy
                </Button>
              </div>
              <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
                <p className="font-medium">Claude Code</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Run this once in your project root:
                </p>
                <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
                  {`claude mcp add todollm ${window.location.origin}/mcp \\\n  -t http \\\n  -H "Authorization: Bearer ${revealed.plaintext}"`}
                </pre>
              </div>
              <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
                <p className="font-medium">MCP client config</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Add this to your MCP client (Claude Desktop, MCP Inspector,
                  etc.) and the agent will connect to this deployment over HTTP.
                </p>
                <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
                  {`{
  "mcpServers": {
    "todollm": {
      "url": "${window.location.origin}/mcp",
      "headers": {
        "Authorization": "Bearer ${revealed.plaintext}"
      }
    }
  }
}`}
                </pre>
                <p className="mt-2 text-xs text-muted-foreground">
                  This key is bound to project{" "}
                  <code>
                    {projectLabelById.get(revealed.project_id) ??
                      `${revealed.project_id.slice(0, 8)}…`}
                  </code>{" "}
                  - tools auto-target it.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => copyKey(revealed.plaintext)}
              >
                <Copy className="size-4" />
                Copy
              </Button>
              <Button onClick={() => setRevealed(null)}>I've saved it</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
